import { Link } from 'react-router-dom';
import { AsyncBoundary } from '@/components/AsyncBoundary/AsyncBoundary';
import { Card } from '@/components/Card/Card';
import { DataTable, type Column } from '@/components/DataTable/DataTable';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { CohortStateBadge } from '@/features/admin/components/CohortStateBadge/CohortStateBadge';
import { api } from '@/lib/api/endpoints';
import type { Cohort } from '@/lib/api/types';
import { formatDate } from '@/lib/format';
import { useAsync } from '@/lib/hooks/useAsync';
import styles from './pages.module.scss';

/**
 * Cohort dashboard (US-6.1). Shows the state, pinned source version, and dates
 * per cohort. Source *repository* details are deliberately absent: the source
 * is identified by its version id here, and never by repo or commit (NFR-AP1).
 */
export function OverviewPage() {
  const { state, reload } = useAsync(() => api.cohorts.list().then((result) => result.cohorts), []);

  const columns: ReadonlyArray<Column<Cohort>> = [
    {
      key: 'name',
      header: 'Cohort',
      render: (cohort) => <Link to={`/admin/cohorts/${cohort.id}`}>{cohort.name}</Link>,
    },
    { key: 'state', header: 'State', render: (cohort) => <CohortStateBadge state={cohort.state} /> },
    { key: 'date', header: 'Workshop date', render: (cohort) => formatDate(cohort.workshop_date) },
    {
      key: 'expiry',
      header: 'Access expires',
      secondary: true,
      render: (cohort) => formatDate(cohort.access_expires_at),
    },
    {
      key: 'source',
      header: 'Source version',
      secondary: true,
      render: (cohort) => <code>{cohort.source_id}</code>,
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Overview"
        title="Cohort readiness"
        description="Every cohort, its state, and the content version it is pinned to. A cohort cannot be published while its source has blocking readiness errors."
      />

      <Card title="Cohorts">
        <AsyncBoundary state={state} subject="cohorts" onRetry={reload}>
          {(cohorts) => (
            <DataTable
              caption="Cohorts and their current state"
              columns={columns}
              rows={cohorts}
              rowKey={(cohort) => cohort.id}
              empty={
                <EmptyState
                  title="No cohorts yet"
                  description="Register a workshop source, then create the first cohort against it."
                />
              }
            />
          )}
        </AsyncBoundary>
      </Card>

      <p className={styles.note}>
        Attendee counts, connected presenters, and completion summaries join this table once the
        corresponding endpoints land (US-6.1).
      </p>
    </>
  );
}
